import { isNaN, isNil } from 'lodash'

// Moteur d'interprétation des formules d'indicateurs RPQS.
//
// Format : ([INT.P152.1]/[INT.P152.2])*100
//  - les valeurs entre [] référencent un autre indicateur du même RPQS,
//    sauf si elles commencent par 'REF.' (calcul spécifique applicatif, ex. REF.001).
//  - opérateurs gérés : + - * / et parenthèses (évaluation par descente récursive).
//  - une référence peut porter une valeur par défaut après un '|' :
//    [INT.D101.1|0]+[INT.D101.2|0] calcule même si les deux champs sont vides (0 + 0).
//
// Règle métier : si une référence ([..]) sans valeur par défaut n'est pas résolue (valeur
// absente, REF non calculable, division par zéro…), AUCUNE valeur n'est produite (undefined).

// Sentinelle interne : une référence n'a pas pu être résolue → on abandonne tout le calcul.
const UNRESOLVED = Symbol('unresolved')

const OPS = {
    '+': (a, b) => a + b,
    '-': (a, b) => a - b,
    '*': (a, b) => a * b,
    '/': (a, b) => a / b,
}

// Découpe la formule en tokens via une regex (réf entre [], nombre, ou opérateur/parenthèse).
const TOKEN_REGEX = /\[[^\]]*\]|[0-9]+(?:[.,][0-9]+)?|[-+*/()]/g

const parseNumber = raw => parseFloat(`${raw}`.replace(',', '.'))

// [CODE] ou [CODE|valeurParDefaut]
const parseRef = raw => {
    const [code, fallback] = raw.slice(1, -1).split('|')
    return { type: 'ref', value: code.trim(), fallback: isNil(fallback) ? undefined : parseNumber(fallback) }
}

const tokenize = formula => (formula.match(TOKEN_REGEX) || []).map(raw => {
    if (raw.startsWith('[')) return parseRef(raw)
    if (OPS[raw] || raw === '(' || raw === ')') return { type: 'op', value: raw }
    return { type: 'num', value: parseNumber(raw) }
})

// Chaque parse* est pur : il consomme un préfixe des tokens et renvoie { value, rest }.
// parseExpression est injecté en paramètre pour gérer les parenthèses sans référence
// circulaire avant définition (récursion mutuelle factor <-> expression).

// factor := number | '[' ref ']' | '(' expression ')' | '-' factor
const parseFactor = (tokens, resolveRef, parseExpression) => {
    const [token, ...rest] = tokens
    if (!token) throw new Error('Formule invalide : fin inattendue')
    if (token.type === 'op' && token.value === '(') {
        const inner = parseExpression(rest, resolveRef)
        const [closing, ...afterParen] = inner.rest
        if (closing?.value !== ')') throw new Error('Formule invalide : parenthèse non fermée')
        return { value: inner.value, rest: afterParen }
    }
    if (token.type === 'op' && token.value === '-') {
        const factor = parseFactor(rest, resolveRef, parseExpression)
        return { value: -factor.value, rest: factor.rest }
    }
    if (token.type === 'num') return { value: token.value, rest }
    if (token.type === 'ref') {
        const resolved = resolveRef(token.value)
        const num = Number(resolved)
        if (isNil(resolved) || resolved === '' || isNaN(num)) {
            if (isNil(token.fallback) || isNaN(token.fallback)) throw UNRESOLVED
            return { value: token.fallback, rest }
        }
        return { value: num, rest }
    }
    throw new Error('Formule invalide')
}

// Niveau binaire générique : applique récursivement les opérateurs de même priorité.
const parseBinary = (operators, parseOperand) => (tokens, resolveRef, parseExpression) => {
    const consume = ({ value, rest }) => {
        const [token, ...after] = rest
        if (token?.type === 'op' && operators.includes(token.value)) {
            const right = parseOperand(after, resolveRef, parseExpression)
            return consume({ value: OPS[token.value](value, right.value), rest: right.rest })
        }
        return { value, rest }
    }
    return consume(parseOperand(tokens, resolveRef, parseExpression))
}

// term := factor (('*'|'/') factor)*
const parseTerm = parseBinary(['*', '/'], parseFactor)
// expression := term (('+'|'-') term)* — s'injecte elle-même pour résoudre les parenthèses.
const parseExpressionLevel = parseBinary(['+', '-'], parseTerm)
const parseExpression = (tokens, resolveRef) => parseExpressionLevel(tokens, resolveRef, parseExpression)

const evaluate = (tokens, resolveRef) => {
    const { value, rest } = parseExpression(tokens, resolveRef)
    if (rest.length) throw new Error('Formule invalide : éléments restants')
    return value
}

// Résout une formule d'indicateur.
//  - formula : ex. '([INT.P152.1]/[INT.P152.2])*100'
//  - getValue(code) : valeur d'un indicateur du même RPQS (number | undefined)
//  - refValues : map des références 'REF.xxx' déjà calculées (ex. { 'REF.001': 1234 })
// Renvoie la valeur calculée, ou undefined si non calculable.
export const resolveFormula = (formula, { getValue = () => undefined, refValues = {} } = {}) => {
    if (!formula) return undefined
    const resolveRef = ref => (ref.startsWith('REF.') ? refValues[ref] : getValue(ref))
    try {
        const value = evaluate(tokenize(formula), resolveRef)
        return Number.isFinite(value) ? value : undefined // ex. division par zéro → Infinity/NaN
    } catch (e) {
        return undefined // UNRESOLVED ou formule invalide → pas de valeur (règle métier)
    }
}

// REF.001 : somme des "population totale INSEE" des communes de l'entité de gestion,
// pour l'année de déclaration du RPQS. Le cityCode de link_cities correspond directement
// au cityCode des populations renvoyées par /city/population.
// Les communes de l'entité absentes du référentiel des communes (données incohérentes) sont
// ignorées. Règle métier : si une commune connue n'a pas de population INSEE, on ne calcule pas.
export const computeRef001 = ({ linkCities = [], cities = [], citiesPopulations = [], year } = {}) => {
    if (!linkCities.length) return undefined
    const knownCityCodes = new Set(cities.map(c => `${c.code}`))
    const knownCities = linkCities.filter(lc => knownCityCodes.has(`${lc.cityCode}`))
    if (!knownCities.length) return undefined
    const inseeTotals = knownCities.map(lc => {
        const population = citiesPopulations.find(p => `${p.cityCode}` === `${lc.cityCode}` && (isNil(year) || p.year === year))
        return population?.inseeTotal
    })
    if (inseeTotals.some(isNil)) return undefined
    return inseeTotals.reduce((sum, value) => sum + value, 0)
}